import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProfileUpdate } from '../hooks/useProfileUpdate';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type DensityMode = 'comfortable' | 'normal' | 'compact';

interface ThemeContextType {
  themeMode: ThemeMode;
  effectiveTheme: 'light' | 'dark';
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  toggleTheme: () => Promise<void>;
  densityMode: DensityMode;
  setDensityMode: (mode: DensityMode) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

const THEME_STORAGE_KEY = 'indexalpha-theme-preference';
const DENSITY_STORAGE_KEY = 'indexalpha-density-preference';

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, userAccount } = useAuth();
  const { updateProfile } = useProfileUpdate();
  const [isLoading, setIsLoading] = useState(false);

  // Track pending theme updates to prevent sync effect from reverting user changes
  // This ref holds the theme value that was just set by the user, cleared when userAccount syncs
  const pendingThemeRef = useRef<ThemeMode | null>(null);

  // Get system preference (safely handle SSR/build-time)
  const prefersDarkMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Initialize theme from localStorage first (synchronous), then sync with userAccount
  const getStoredTheme = (): ThemeMode => {
    if (typeof window !== 'undefined') {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
      if (storedTheme && ['light', 'dark', 'auto'].includes(storedTheme)) {
        return storedTheme;
      }
    }
    return 'light';
  };

  // Initialize from localStorage immediately (avoids flash)
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredTheme);

  // Initialize density mode from user account or localStorage
  const getInitialDensity = useCallback((): DensityMode => {
    // First priority: user account preference
    if (userAccount?.preferences?.density) {
      return userAccount.preferences.density;
    }

    // Second priority: localStorage (safely handle SSR/build-time)
    if (typeof window !== 'undefined') {
      const storedDensity = localStorage.getItem(DENSITY_STORAGE_KEY) as DensityMode;
      if (storedDensity && ['comfortable', 'normal', 'compact'].includes(storedDensity)) {
        return storedDensity;
      }
    }

    // Default to compact (data-dense for financial professionals)
    return 'compact';
  }, [userAccount]);

  const [densityMode, setDensityModeState] = useState<DensityMode>(getInitialDensity);

  // Calculate effective theme based on mode
  const effectiveTheme: 'light' | 'dark' = useMemo(() => {
    if (themeMode === 'auto') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, prefersDarkMode]);

  // Sync theme with user account when user logs in
  // Only sync FROM userAccount if user is authenticated AND has a stored preference
  // localStorage takes precedence for anonymous users
  useEffect(() => {
    const accountTheme = userAccount?.preferences?.theme;

    // Only sync from account if user is authenticated and has a theme preference
    if (!user || !accountTheme) return;

    // If the account theme matches our pending change, clear the pending ref
    if (pendingThemeRef.current && accountTheme === pendingThemeRef.current) {
      pendingThemeRef.current = null;
      return;
    }

    // If there's a pending change that hasn't synced yet, don't revert
    if (pendingThemeRef.current) {
      return;
    }

    // **An explicit choice on THIS device wins** ([[BUGS #203]]).
    //
    // `pendingThemeRef` guards the current session, but it is an in-memory ref
    // — on the next page load it is null. So a stale account value would set
    // state back AND overwrite localStorage with it, destroying the local
    // preference. That is exactly what staging did on every load: the theme is
    // written through the gateway to the staging user document while
    // `userAccount` arrives from the browser's snapshot, which is pinned to
    // production ([[BUGS #190]]) — so the account never learned the choice and
    // reverted it forever.
    //
    // The sync still runs when this device has no stored choice, which is the
    // case it exists for: arriving on a new device and finding your preference
    // already applied.
    const localChoice = typeof window !== 'undefined'
      ? localStorage.getItem(THEME_STORAGE_KEY)
      : null;
    if (localChoice) return;

    if (accountTheme !== themeMode) {
      setThemeModeState(accountTheme);
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, accountTheme);
      }
    }
  }, [user, userAccount?.preferences?.theme, themeMode]);

  // Sync density with user account when it changes
  useEffect(() => {
    if (userAccount?.preferences?.density && userAccount.preferences.density !== densityMode) {
      setDensityModeState(userAccount.preferences.density);
    }
  }, [userAccount?.preferences?.density, densityMode]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (themeMode !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Force re-render when system theme changes and we're in auto mode
      setThemeModeState('auto');
    };

    // Check if addEventListener is supported (it should be in modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  // Update theme preference
  const setThemeMode = useCallback(async (mode: ThemeMode) => {
    setIsLoading(true);
    // Mark this theme as pending so sync effect doesn't revert it
    pendingThemeRef.current = mode;

    try {
      // Update local state immediately for better UX
      setThemeModeState(mode);

      // Safely access localStorage (handle SSR/build-time)
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
      }

      // If user is authenticated, persist to Firestore
      if (user && userAccount) {
        await updateProfile({
          preferences: {
            ...userAccount.preferences,
            theme: mode
          }
        });
      }
    } catch (_error) {
      // Clear pending and revert on error
      pendingThemeRef.current = null;
      setThemeModeState(getStoredTheme());
    } finally {
      setIsLoading(false);
    }
  }, [user, userAccount, updateProfile]);

  // Toggle between light and dark (skip auto)
  const toggleTheme = useCallback(async () => {
    const newMode: ThemeMode = effectiveTheme === 'light' ? 'dark' : 'light';
    await setThemeMode(newMode);
  }, [effectiveTheme, setThemeMode]);

  // Update density preference
  const setDensityMode = useCallback(async (mode: DensityMode) => {
    setIsLoading(true);
    try {
      // Update local state immediately for better UX
      setDensityModeState(mode);

      // Safely access localStorage (handle SSR/build-time)
      if (typeof window !== 'undefined') {
        localStorage.setItem(DENSITY_STORAGE_KEY, mode);
      }

      // If user is authenticated, persist to Firestore
      if (user && userAccount) {
        await updateProfile({
          preferences: {
            ...userAccount.preferences,
            density: mode
          }
        });
      }
    } catch (_error) {
      // Revert on error
      setDensityModeState(getInitialDensity());
    } finally {
      setIsLoading(false);
    }
  }, [user, userAccount, updateProfile, getInitialDensity]);

  const value = useMemo(
    () => ({
      themeMode,
      effectiveTheme,
      setThemeMode,
      toggleTheme,
      densityMode,
      setDensityMode,
      isLoading
    }),
    [themeMode, effectiveTheme, setThemeMode, toggleTheme, densityMode, setDensityMode, isLoading]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
